import { useState, useRef } from "react";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { createCreditTypeService } from "../services/adminCredit.Service"; 
import {CreateCreditTypeDto} from "../models/create-credit-type.models"

const styles = `
  .cct-trigger.p-button {
    background: #00A86B !important;
    border: none !important;
    border-radius: 12px !important;
    font-family: 'Plus Jakarta Sans', sans-serif !important;
    font-weight: 600 !important;
    font-size: 0.82rem !important;
    padding: 10px 18px !important;
    box-shadow: 0 4px 14px rgba(0,168,107,0.28) !important;
  }
  .cct-trigger.p-button:hover {
    background: #009960 !important;
  }

  .p-dialog.cct-dialog {
    border-radius: 16px !important;
    overflow: hidden;
    font-family: 'Plus Jakarta Sans', sans-serif !important;
  }
  .p-dialog.cct-dialog .p-dialog-header {
    background: #ffffff !important;
    padding: 20px 24px 14px !important;
    border-bottom: 1px solid #f1f3f5 !important;
  }
  .p-dialog.cct-dialog .p-dialog-title {
    font-size: 1.02rem !important;
    font-weight: 800 !important;
    color: #1a1d23 !important;
  }
  .p-dialog.cct-dialog .p-dialog-content {
    padding: 20px 24px 8px !important;
    background: #ffffff !important;
  }
  .p-dialog.cct-dialog .p-dialog-footer {
    padding: 14px 24px 20px !important;
    border-top: 1px solid #f1f3f5 !important;
    background: #f8fafc !important;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
  }

  .cct-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }
  .cct-full { grid-column: 1 / -1; }
  .cct-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 16px;
  }
  .cct-label {
    font-size: 0.76rem;
    font-weight: 600;
    color: #374151;
  }
  .cct-error {
    font-size: 0.7rem;
    color: #ef4444;
  }
  .cct-field .p-inputtext,
  .cct-field .p-inputnumber-input {
    background: #ffffff !important;
    color: #1a1d23 !important;
    border-radius: 10px !important;
    border: 1px solid #e2e8f0 !important;
    font-family: 'Plus Jakarta Sans', sans-serif !important;
    font-size: 0.85rem !important;
    padding: 10px 12px !important;
    width: 100%;
  }
  .cct-field .p-inputtext::placeholder {
    color: #94a3b8 !important;
  }
  .cct-field .p-inputnumber { width: 100%; }
  .cct-field .p-inputtext:focus,
  .cct-field .p-inputnumber-input:focus {
    border-color: #00A86B !important;
    box-shadow: 0 0 0 1px #00A86B !important;
  }
  .cct-field .p-inputtext.p-invalid,
  .cct-field .p-inputnumber-input.p-invalid {
    border-color: #ef4444 !important;
  }

  .cct-btn-cancel.p-button {
    background: #ffffff !important;
    border: 1px solid #e2e8f0 !important;
    color: #64748b !important;
    border-radius: 10px !important;
    font-family: 'Plus Jakarta Sans', sans-serif !important;
    font-weight: 600 !important;
    font-size: 0.82rem !important;
    padding: 10px 20px !important;
  }
  .cct-btn-confirm.p-button {
    background: #00A86B !important;
    border: none !important;
    color: #fff !important;
    border-radius: 10px !important;
    font-family: 'Plus Jakarta Sans', sans-serif !important;
    font-weight: 600 !important;
    font-size: 0.82rem !important;
    padding: 10px 20px !important;
    box-shadow: 0 4px 14px rgba(0,168,107,0.28) !important;
  }
  .cct-btn-confirm.p-button:enabled:hover {
    background: #009960 !important;
  }

  @media (max-width: 640px) {
    .cct-grid { grid-template-columns: 1fr; }
  }
`;

interface FormState {
  name: string;
  code: string;
  defaultInterestRate: number | null;
  minTermMonths: number | null;
  maxTermMonths: number | null;
  minAmount: number | null;
  maxAmount: number | null;
}

const initialState: FormState = {
  name: "",
  code: "",
  defaultInterestRate: null,
  minTermMonths: null,
  maxTermMonths: null,
  minAmount: null,
  maxAmount: null,
};

interface Props {
  onCreated?: () => void;
}

function CreateCreditTypeForm({ onCreated }: Props): JSX.Element {
  const toast = useRef<Toast>(null);
  const [visible, setVisible] = useState(false);
  const [form, setForm] = useState<FormState>(initialState);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [submitting, setSubmitting] = useState(false);

  const setField = (field: keyof FormState, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const openDialog = () => {
    setForm(initialState);
    setErrors({});
    setVisible(true);
  };

  const closeDialog = () => {
    if (!submitting) setVisible(false);
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof FormState, string>> = {};

    if (!form.name || form.name.length > 100) {
      newErrors.name = "Nombre requerido (máx 100 caracteres)";
    }
    if (!form.code || form.code.length > 20) {
      newErrors.code = "Código requerido (máx 20 caracteres)";
    }
    if (form.defaultInterestRate === null || form.defaultInterestRate < 0 || form.defaultInterestRate > 100) {
      newErrors.defaultInterestRate = "Tasa entre 0 y 100";
    }
    if (form.minTermMonths === null || form.minTermMonths < 1) {
      newErrors.minTermMonths = "Plazo mínimo requerido";
    }
    if (form.maxTermMonths === null || form.maxTermMonths < 1) {
      newErrors.maxTermMonths = "Plazo máximo requerido";
    }
    if (
      form.minTermMonths !== null &&
      form.maxTermMonths !== null &&
      form.maxTermMonths < form.minTermMonths
    ) {
      newErrors.maxTermMonths = "Debe ser mayor o igual al plazo mínimo";
    }
    if (form.minAmount === null || form.minAmount <= 0) {
      newErrors.minAmount = "Monto mínimo requerido";
    }
    if (form.maxAmount === null || form.maxAmount <= 0) {
      newErrors.maxAmount = "Monto máximo requerido";
    }
    if (
      form.minAmount !== null &&
      form.maxAmount !== null &&
      form.maxAmount < form.minAmount
    ) {
      newErrors.maxAmount = "Debe ser mayor o igual al monto mínimo";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      toast.current?.show({
        severity: "warn",
        summary: "Formulario incompleto",
        detail: "Revisa los campos marcados en rojo",
        life: 3500,
      });
      return;
    }

    setSubmitting(true);
    try {
      const payload: CreateCreditTypeDto = {
        name: form.name,
        code: form.code,
        defaultInterestRate: form.defaultInterestRate!,
        minTermMonths: form.minTermMonths!,
        maxTermMonths: form.maxTermMonths!,
        minAmount: form.minAmount!,
        maxAmount: form.maxAmount!,
        requiresCosigner: false,
      };

      const response: any = await createCreditTypeService(payload);

      if (response?.statusCode && response.statusCode >= 300) {
        toast.current?.show({
          severity: "error",
          summary: "No se pudo crear",
          detail: response.message ?? "Intenta nuevamente",
          life: 4000,
        });
        return;
      }

      toast.current?.show({
        severity: "success",
        summary: "¡Listo!",
        detail: "Tipo de crédito creado correctamente",
        life: 3000,
      });

      setVisible(false);
      onCreated?.();
    } catch (err: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: err?.response?.data?.message ?? "No se pudo crear el tipo de crédito",
        life: 4000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <style>{styles}</style>
      <Toast ref={toast} />

      <Button
        label="Nuevo tipo de crédito"
        icon="pi pi-plus"
        className="cct-trigger"
        onClick={openDialog}
      />

      <Dialog
        header="Crear tipo de crédito"
        visible={visible}
        onHide={closeDialog}
        className="cct-dialog"
        style={{ width: "560px", maxWidth: "95vw" }}
        modal
        footer={
          <>
            <Button
              type="button"
              label="Cancelar"
              className="cct-btn-cancel"
              onClick={closeDialog}
              disabled={submitting}
            />
            <Button
              type="button"
              label={submitting ? "Creando..." : "Crear tipo de crédito"}
              className="cct-btn-confirm"
              onClick={handleSubmit}
              disabled={submitting}
            />
          </>
        }
      >
        <div className="cct-grid">
          <div className="cct-field cct-full">
            <label className="cct-label">Nombre<span style={{ color: "#ef4444" }}>*</span></label>
            <InputText
              value={form.name}
              onChange={(e) => setField("name", e.target.value)}
              className={errors.name ? "p-invalid" : ""}
              placeholder="Ej: Crédito Vehicular"
            />
            {errors.name && <span className="cct-error">{errors.name}</span>}
          </div>

          <div className="cct-field">
            <label className="cct-label">Código<span style={{ color: "#ef4444" }}>*</span></label>
            <InputText
              value={form.code}
              onChange={(e) => setField("code", e.target.value.toUpperCase())}
              className={errors.code ? "p-invalid" : ""}
              placeholder="Ej: VEH"
            />
            {errors.code && <span className="cct-error">{errors.code}</span>}
          </div>

          <div className="cct-field">
            <label className="cct-label">Tasa de interés (%)<span style={{ color: "#ef4444" }}>*</span></label>
            <InputNumber
              value={form.defaultInterestRate}
              onValueChange={(e) => setField("defaultInterestRate", e.value ?? null)}
              minFractionDigits={2}
              maxFractionDigits={2}
              suffix="%"
              className={errors.defaultInterestRate ? "p-invalid" : ""}
            />
            {errors.defaultInterestRate && <span className="cct-error">{errors.defaultInterestRate}</span>}
          </div>

          <div className="cct-field">
            <label className="cct-label">Plazo mínimo (meses)<span style={{ color: "#ef4444" }}>*</span></label>
            <InputNumber
              value={form.minTermMonths}
              onValueChange={(e) => setField("minTermMonths", e.value ?? null)}
              className={errors.minTermMonths ? "p-invalid" : ""}
            />
            {errors.minTermMonths && <span className="cct-error">{errors.minTermMonths}</span>}
          </div>

          <div className="cct-field">
            <label className="cct-label">Plazo máximo (meses)<span style={{ color: "#ef4444" }}>*</span></label>
            <InputNumber
              value={form.maxTermMonths}
              onValueChange={(e) => setField("maxTermMonths", e.value ?? null)}
              className={errors.maxTermMonths ? "p-invalid" : ""}
            />
            {errors.maxTermMonths && <span className="cct-error">{errors.maxTermMonths}</span>}
          </div>

          <div className="cct-field">
            <label className="cct-label">Monto mínimo<span style={{ color: "#ef4444" }}>*</span></label>
            <InputNumber
              value={form.minAmount}
              onValueChange={(e) => setField("minAmount", e.value ?? null)}
              mode="currency"
              currency="COP"
              locale="es-CO"
              maxFractionDigits={0}
              className={errors.minAmount ? "p-invalid" : ""}
            />
            {errors.minAmount && <span className="cct-error">{errors.minAmount}</span>}
          </div>

          <div className="cct-field">
            <label className="cct-label">Monto máximo<span style={{ color: "#ef4444" }}>*</span></label>
            <InputNumber
              value={form.maxAmount}
              onValueChange={(e) => setField("maxAmount", e.value ?? null)}
              mode="currency"
              currency="COP"
              locale="es-CO"
              maxFractionDigits={0}
              className={errors.maxAmount ? "p-invalid" : ""}
            />
            {errors.maxAmount && <span className="cct-error">{errors.maxAmount}</span>}
          </div>
        </div>
      </Dialog>
    </>
  );
}

export default CreateCreditTypeForm;